import Vue from 'vue'
import VueRouter from 'vue-router'

import { canNavigate } from '@/libs/acl/routeProtection'
import { isUserLoggedIn, getUserData, getHomeRouteForLoggedInUser } from '@/auth/utils'
import ability from '@/libs/acl/ability'
import axiosIns from '@/libs/axios'

import auth from './routes/auth'
import community from './routes/community'
import stats from './routes/stats'
import subscritpion from './routes/subscription'
import faq from './routes/faq'
import dashboard from './routes/dashboard'

Vue.use(VueRouter)

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    { path: '/', redirect: { name: 'community' } },
    ...auth,
    ...community,
    ...stats,
    ...subscritpion,
    ...faq,
    ...dashboard,
    {
      path: '*',
      redirect: 'error-404',
    },
  ],
})

// `userData.ability` is only ever written on login; a page reload rebuilds
// the CASL ability straight from that cached localStorage value (see
// src/libs/acl/ability.js) without checking it against the server. Anyone
// can edit it in devtools, and it also just goes stale if a role changes
// server-side mid-session. This re-fetches it from the authenticated
// `/api/me/` endpoint once per app load -- before the first route resolves
// -- so the ability driving route/menu gating always reflects what the
// server currently thinks, not a possibly-tampered-with or stale local copy.
// This is still only UX-level hardening, not a security boundary: real
// authorization is enforced per-request by dashboard_backend regardless of
// what the client's ability object says (see docs/architecture.md §6).
let hasSyncedAbility = false

router.beforeEach(async (to, _, next) => {
  if (isUserLoggedIn() && !hasSyncedAbility) {
    hasSyncedAbility = true
    try {
      const { data } = await axiosIns.get('/api/me/')
      localStorage.setItem('userData', JSON.stringify(data.user))
      ability.update(data.user.ability)
    } catch {
      // A 401 here is already handled by jwtService's response interceptor
      // (refresh-and-retry, or logout on failure) -- isUserLoggedIn() below
      // picks up the result either way. Any other error just leaves the
      // last-known (login-time) ability in place rather than blocking nav.
    }
  }

  const isLoggedIn = isUserLoggedIn()

  if (!canNavigate(to)) {
    // Redirect to login if not logged in
    if (!isLoggedIn) return next({ name: 'auth-login' })

    // If logged in => not authorized
    return next({ name: 'misc-not-authorized' })
  }

  // Redirect if logged in
  if (to.meta.redirectIfLoggedIn && isLoggedIn) {
    const userData = getUserData()
    next(getHomeRouteForLoggedInUser(userData ? userData.role : null))
  }

  return next()
})

export default router
